import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Location from '@solvhealth/types/interfaces/Location';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import { Text } from '@solvhealth/jigsaw-v1';
import isEmpty from 'lodash/isEmpty';
import SlotComponent from './Views/components/SlotStates';
import SolvSkeleton from '~/components/SolvDesignSystem/Loading/SolvSkeleton';
import { useSolvSelector } from '~/reducers';

const SLOTS_GAP = 8;
const ROWS_PER_CHUNK = 2;

const Container = styled.div.withConfig({ componentId: 'sc-19y5683-0' })<{
  $canSlotsFitOnOneRow: boolean;
  $hasMinHeight: boolean;
  $slotHeight: number;
  $slotWidth: number;
}>`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${(props) => props.$slotWidth}px);
  grid-gap: ${SLOTS_GAP}px;
  ${(props) =>
    props.$hasMinHeight &&
    `min-height: ${
      props.$slotHeight * (props.$canSlotsFitOnOneRow ? 1 : ROWS_PER_CHUNK) +
      SLOTS_GAP * (ROWS_PER_CHUNK - 1)
    }px;`}
  justify-content: center;
`;

const StyledSlot = styled(SlotComponent).withConfig({ componentId: 'sc-19y5683-1' })<{ $width: number; $height: number }>`
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  line-height: ${(props) => props.$height}px;
  padding: 0;
  border-radius: 4px;
  font-size: ${(props) => (props.$height >= 40 ? 14 : 12)}px;
`;

const StyledText = styled(Text).withConfig({ componentId: 'sc-19y5683-2' })<{ $width: number; $height: number }>`
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  line-height: ${(props) => props.$height}px;
  padding: 0;
  font-size: 14px;
  text-align: center;
  text-decoration: underline;
  cursor: pointer;
`;

type SlotsProps = {
  isCTA?: boolean;
  location: Location;
  slots: Slot[];
  selectedSlots?: Slot[];
  handleSelectSlot: (slot: Slot, location: Location) => void;
  resetScrollOnFewer?: boolean;
  showSkeletonLoaders?: boolean;
  slotsHeight?: number;
  slotsWidth?: number;
};

const isSelectedSlot = (slot: Slot, selectedSlots: Slot[]) => {
  return selectedSlots.some((s: any) => s.appointmentDate === slot.appointmentDate);
};

const Slots = (props: SlotsProps) => {
  const {
    isCTA = false,
    slots,
    location,
    location: { timeZone },
    handleSelectSlot,
    resetScrollOnFewer,
    selectedSlots = [],
    showSkeletonLoaders = false,
    slotsHeight = 40,
    slotsWidth = 78,
  } = props;

  const viewport = useSolvSelector((state) => state.runtime.viewport);

  const containerRef = useRef<HTMLDivElement>(null);

  // A "chunk" of slots is added when the user selects show more; there are a specific number of rows per chunk (ROWS_PER_CHUNK)
  const [chunksToDisplay, setChunksToDisplay] = useState(1);
  const [containerWidth, setContainerWidth] = useState(containerRef.current?.offsetWidth);

  const slotsPerRow = useMemo(() => {
    if (containerWidth) {
      // SLOTS_WIDTH * (slotsPerRow) + SLOTS_GAP * (slotsPerRow - 1) < containerWidth
      return Math.floor((containerWidth + SLOTS_GAP) / (slotsWidth + SLOTS_GAP));
    }
    // Default to 4
    return 4;
  }, [containerWidth, slotsWidth]);

  const numSlotsToDisplay = useMemo(() => {
    return chunksToDisplay * ROWS_PER_CHUNK * slotsPerRow;
  }, [chunksToDisplay, slotsPerRow]);

  const minimumChunksToDisplay = useMemo(
    () =>
      selectedSlots.reduce((max, curr) => {
        const slotIndex = slots.indexOf(curr);
        const slotRow = Math.ceil((slotIndex + 1) / slotsPerRow);

        let minimumChunk = Math.ceil(slotRow / ROWS_PER_CHUNK);

        // This is used to add a chunk if the slot position will be hidden by view more
        const hiddenByViewMore =
          (slotIndex + 1) % (slotsPerRow * ROWS_PER_CHUNK) === 0 &&
          slots.length > minimumChunk * ROWS_PER_CHUNK * slotsPerRow;
        if (hiddenByViewMore) minimumChunk++;

        return Math.max(max, minimumChunk);
      }, 1),
    [selectedSlots, slots, slotsPerRow]
  );

  useEffect(() => {
    setContainerWidth(containerRef.current?.offsetWidth);
  }, [viewport]);

  useEffect(() => {
    setChunksToDisplay((prev) => Math.max(minimumChunksToDisplay, prev));
  }, [minimumChunksToDisplay]);

  const viewMore = useCallback(() => {
    if (slots.length > numSlotsToDisplay) {
      setChunksToDisplay((prev) => prev + 1);
    }
  }, [numSlotsToDisplay, slots.length]);

  const viewFewer = () => {
    if (resetScrollOnFewer && containerRef.current) {
      window.scrollTo({ behavior: 'smooth', top: containerRef.current.offsetTop });
    }
    setChunksToDisplay(minimumChunksToDisplay);
  };

  const showViewMore = useMemo(() => {
    return slots.length > numSlotsToDisplay;
  }, [numSlotsToDisplay, slots.length]);

  const showViewLess = useMemo(() => {
    return chunksToDisplay > minimumChunksToDisplay && slots.length <= numSlotsToDisplay;
  }, [chunksToDisplay, minimumChunksToDisplay, numSlotsToDisplay, slots.length]);

  // Reset row display when location changes
  useEffect(() => {
    setChunksToDisplay(1);
  }, [location]);

  return (
    <Container
      $canSlotsFitOnOneRow={slots.length <= slotsPerRow}
      $hasMinHeight={showSkeletonLoaders}
      $slotHeight={slotsHeight}
      $slotWidth={slotsWidth}
      ref={containerRef}
    >
      {!isEmpty(slots) ? (
        <>
          {slots.slice(0, showViewMore ? numSlotsToDisplay - 1 : slots.length).map((slot, i) => (
            <StyledSlot
              $height={slotsHeight}
              $width={slotsWidth}
              isCTA={isCTA}
              isSelected={isSelectedSlot(slot, selectedSlots)}
              key={`${slot.appointmentDate} - ${i}`}
              onSelect={() => handleSelectSlot(slot, location)}
              slot={slot}
              timeZone={timeZone}
            />
          ))}
          {showViewMore && (
            <StyledText
              $height={slotsHeight}
              $width={slotsWidth}
              onClick={viewMore}
              textColor="crunchberry-dark"
              variant="body2"
            >
              view more
            </StyledText>
          )}
          {showViewLess && (
            <StyledText
              $height={slotsHeight}
              $width={slotsWidth}
              onClick={viewFewer}
              textColor="crunchberry-dark"
              variant="body2"
            >
              view fewer
            </StyledText>
          )}
        </>
      ) : (
        showSkeletonLoaders &&
        Array(slotsPerRow * ROWS_PER_CHUNK)
          .fill(null)
          .map((_, i) => (
            <SolvSkeleton height={slotsHeight} key={`skeleton-slot-${i}`} width={slotsWidth} />
          ))
      )}
    </Container>
  );
};

export default Slots;
